import Axios from 'axios';
import { push } from 'react-router-redux';
import { toastr } from 'react-redux-toastr';
import { cloneDeep, get, isEmpty, set } from 'lodash';
import { formValueSelector } from 'redux-form';
import queryString from '../../../helpers/queryString';
import handleErrorResponse from '../../../helpers/handleErrorResponse';
import { isOwner } from '../../auth/redux/authActions';
import * as productActions from './productReducer';
import {
  sendCustomerLoginEmail,
  sendEndorsementEmail,
} from '../../policy/redux/policyActions';
import errorResponseToArray from '../../../helpers/errorResponseToArray';
import getAge from '../../../helpers/getAge';
import { parseISO, isValid, parse, format, differenceInDays } from 'date-fns';

const defaultIncludes = [];
const TAX_EXEMPT_POSTCODES = ['BF', 'GX', 'GY', 'IM', 'JE'];

const dateFromApi = (date) => {
  if (!date) {
    return '';
  }
  const parsedDate = parseISO(date);
  return isValid(parsedDate) ? format(parsedDate, 'dd/mm/yyyy') : '';
};

export const dateToApi = (date) => {
  if (!date) {
    return '';
  }
  const parsedDate = parse(date, 'dd/mm/yyyy', new Date());
  if (!isValid(parsedDate)) {
    return '';
  }
  const dateFormat = format(parsedDate, 'yyyy-mm-dd');
  const timeFormat = format(parsedDate, 'HH:mm:ss');
  return dateFormat + 'T' + timeFormat + '.000Z';
};

function postcodeTypeToApi(traveller) {
  const postcode = traveller?.address?.postcode;
  const postcodePrefix = postcode
    ?.trim()
    .substring(0, 2)
    .toUpperCase();
  return TAX_EXEMPT_POSTCODES.includes(postcodePrefix) ? 'uk_and_balliwicks' : 'uk';
}

const fromApi = (application) => {
  if (get(application, 'data.attributes.metadata.adults')) {
    application.data.attributes.metadata.adults = application.data.attributes.metadata.adults.map(
      (adult) => {
        const adultDob = dateFromApi(adult.dob);
        adult.dob = adultDob;
        if (adultDob) {
          adult.age = getAge(adult.dob);
        }
        return adult;
      },
    );
  }

  if (get(application, 'data.attributes.metadata.children')) {
    application.data.attributes.metadata.children = application.data.attributes.metadata.children.map(
      (child) => {
        const childDob = dateFromApi(child.dob);
        child.dob = childDob;
        if (childDob) {
          child.age = getAge(child.dob);
        }
        return child;
      },
    );
  }
  return application;
};

const toApi = (application) => {
  if (get(application, 'data.attributes.metadata.adults')) {
    application.data.attributes.metadata.adults = application.data.attributes.metadata.adults.map(
      (adult) => {
        adult.dob = dateToApi(adult.dob);
        if (adult.dob) {
          delete adult.id;
          delete adult.age;
        }
        return adult;
      },
    );
    application.data.attributes.metadata.postcode_type = postcodeTypeToApi(
      get(application, 'data.attributes.metadata.adults')[0],
    );
  }

  if (get(application, 'data.attributes.metadata.children')) {
    application.data.attributes.metadata.children = application.data.attributes.metadata.children.map(
      (child) => {
        child.dob = dateToApi(child.dob);
        if (child.dob) {
          delete child.id;
          delete child.age;
        }
        return child;
      },
    );
  }

  if (get(application, 'data.attributes.metadata.travellers')) {
    application.data.attributes.metadata.travellers = application.data.attributes.metadata.travellers.map(
      (traveller) => {
        traveller.dob = dateToApi(traveller.dob);

        return traveller;
      },
    );
  }

  return application;
};

const isCalculating = (status) => {
  return {
    type: productActions.IS_CALCULATING,
    status,
  };
};

const isPremiumAdjusting = (status) => {
  return {
    type: productActions.IS_PREMIUM_ADJUSTING,
    status,
  };
};

const clearData = () => {
  return {
    type: productActions.CLEAR_DATA,
  };
};

const isUpdatingPremiumIndication = (status) => {
  return {
    type: productActions.IS_UPDATING_PREMIUM_INDICATION,
    status,
  };
};

const submitSucceeded = (status) => {
  return {
    type: productActions.SUBMIT_SUCCEEDED,
    status,
  };
};

const isSaving = (status) => {
  return {
    type: productActions.IS_SAVING,
    status,
  };
};

const isIssuing = (status) => {
  return {
    type: productActions.IS_ISSUING,
    status,
  };
};

const showPaymentIframe = (show, transactionId) => {
  return {
    type: productActions.SHOW_PAYMENT_IFRAME,
    data: {
      show,
      transactionId,
    },
  };
};

const setSource = (data) => {
  return {
    type: productActions.SET_SOURCE,
    data,
  };
};

const setRegions = (data) => {
  return {
    type: productActions.SET_REGIONS,
    data,
  };
};

const setSchemes = (data) => {
  return {
    type: productActions.SET_SCHEMES,
    data,
  };
};

const setSchemesWithIncludes = (data) => {
  return {
    type: productActions.SET_SCHEMES_WITH_INCLUDES,
    data,
  };
};

const setSignpost = (content) => {
  return {
    type: productActions.SET_SIGNPOST,
    data: content,
  };
};

const setProduct = (data) => {
  return {
    type: productActions.SET_PRODUCT,
    data,
  };
};

const setPremiumOverrideCalculation = (data) => {
  return {
    type: productActions.SET_PREMIUM_OVERRIDE_CALCULATION,
    data,
  };
};

const setPremiumIndication = (premium) => {
  return {
    type: productActions.SET_PREMIUM_INDICATION,
    premium,
  };
};

const clearNextQuoteReference = () => {
  return {
    type: productActions.SET_NEXT_QUOTE_REFERENCE,
    data: '',
  };
};

const setNextQuoteReference = (data) => {
  return {
    type: productActions.SET_NEXT_QUOTE_REFERENCE,
    data,
  };
};

const setQuote = (data) => {
  return {
    type: productActions.SET_QUOTE,
    data,
  };
};
const setQuotes = (data) => {
  return {
    type: productActions.SET_QUOTES,
    data,
  };
};

const clearQuotes = () => {
  return {
    type: productActions.CLEAR_QUOTES,
  };
};

const setPremiumIndicationError = (data) => {
  return {
    type: productActions.SET_PREMIUM_INDICATION_ERROR,
    data,
  };
};

const getSource = (id) => {
  return (dispatch) => {
    return Axios.get(`sources/${id}?include=broker,channel`).then((res) => {
      dispatch(setSource(res.data.data));
      return true;
    });
  };
};

const getRegions = () => {
  return (dispatch) => {
    return Axios.get('products/regions').then((res) => {
      dispatch(setRegions(res.data.data));
      return true;
    });
  };
};

const getProductDocuments = () => {
  return () => {
    return Axios.get('product-documents').then((res) => {
      return res.data.meta;
    });
  };
};

const getNextQuoteReference = () => {
  return (dispatch) => {
    dispatch(clearNextQuoteReference());
    return Axios.get('/policies/applications/quote-reference').then((res) => {
      dispatch(setNextQuoteReference(res.data.meta.quote_reference));
      return true;
    });
  };
};

const getSchemes = (productId, filter) => {
  return (dispatch) => {
    const endpoint = `products/schemes?filter[product]=${productId}${
      filter ? `&filter[status]=${filter}` : '&page[limit]=500'
    }`;
    return Axios.get(endpoint).then((res) => {
      dispatch(setSchemes(res.data.data));
      return true;
    });
  };
};

const getSchemesWithIncludes = (productId, filter, include) => {
  return (dispatch) => {
    const endpoint = `products/schemes?filter[product]=${productId}${
      filter ? `&filter=${filter}` : ''
    }${include ? `&include=${include}` : ''}`;

    return Axios.get(endpoint).then((res) => {
      dispatch(setSchemesWithIncludes(res.data));
      return true;
    });
  };
};

const getSignpost = (policyApplicationId, signpost) => {
  return () => {
    return Axios.post('public/contents/signpost', {
      meta: {
        name: signpost,
        policy_application_id: policyApplicationId,
      },
    });
  };
};

const processQuotes = (quotes, showQuotesWithErrors, schemeType) => {
  if (quotes.length === 0) {
    return {
      quotes: [],
      signposted: false,
    };
  }

  const sortedPremiums = quotes.reduce(
    (carry, quote) => {
      if (
        !quote.declines.length &&
        !quote.errors.length &&
        quote.premium.is_signposted &&
        quote.premium.gross === 0
      ) {
        carry.non_medical_premiums.push(quote);
        return carry;
      }

      if (
        !quote.declines.length &&
        !quote.errors.length &&
        !quote.premium.is_signposted
      ) {
        carry.non_medical_premiums.push(quote);
        return carry;
      }

      if (!quote.declines.length && !quote.errors.length && quote.premium.is_signposted) {
        carry.medical_premiums.push(quote);
        return carry;
      }

      if (quote.declines.length || quote.errors.length) {
        const errors = quote.errors && quote.errors.length ? quote.errors : [];
        // if on ST with an AMT exclusion then treat the screening error as a medical decline so that the medical decline logic will not fail
        const hasAnnualExclusion = !!errors.find((error) =>
          error.includes('Medical Condition has annual exclusion'),
        );
        const isAnnualQuote = schemeType === 'annual';
        if (!isAnnualQuote && hasAnnualExclusion) {
          carry.medical_declines.push(quote);
          return carry;
        }
        const hasScreeningError = errors.find(
          (error) =>
            error.includes('Medical Condition has winter sports exclusion') ||
            error.includes('Medical Condition has annual exclusion'),
        );
        if (hasScreeningError) {
          carry.screening_errors.push(quote);
          return carry;
        }
        const hasMedicalError = errors.reduce((medErrorCarry, error) => {
          if (
            error.includes('Exceeds maximum medical score') ||
            error.includes('Maximum premium') ||
            error.includes("Medical Declaration Question 'is-terminal' is not covered")
          ) {
            return true;
          }
          return medErrorCarry;
        }, false);
        const declines = quote.declines && quote.declines.length ? quote.declines : [];
        const hasMedicalDecline = declines.reduce((medDeclineCarry, decline) => {
          if (decline.is_medical) {
            return true;
          }
          return medDeclineCarry;
        }, false);
        if (hasMedicalError || hasMedicalDecline) {
          carry.medical_declines.push(quote);
          return carry;
        }
        carry.non_medical_declines.push(quote);
      }
      return carry;
    },
    {
      non_medical_premiums: [],
      medical_premiums: [],
      medical_declines: [],
      screening_errors: [],
      non_medical_declines: [],
    },
  );

  const displayQuotes = quotes.reduce((carry, quote) => {
    const hasErrors = quote.errors && quote.errors.length;
    const hasDeclines = quote.declines && quote.declines.length;
    if (showQuotesWithErrors || (!hasErrors && !hasDeclines)) {
      carry[quote.scheme.id] = { meta: quote };
    }
    return carry;
  }, {});

  if (
    !!sortedPremiums.medical_premiums.length &&
    !sortedPremiums.non_medical_premiums.length
  ) {
    return {
      quotes: displayQuotes,
      signposted: 'medical_premiums_bo',
    };
  }

  if (sortedPremiums.screening_errors.length === quotes.length) {
    return {
      quotes: displayQuotes,
      signposted: 'screening_errors_bo',
    };
  }
  if (sortedPremiums.medical_declines.length === quotes.length) {
    return {
      quotes: displayQuotes,
      signposted: 'medical_declines_bo',
    };
  }
  const totalValidQuotes =
    sortedPremiums.medical_premiums.length + sortedPremiums.non_medical_premiums.length;
  const medicalDeclines =
    sortedPremiums.medical_declines.length + sortedPremiums.screening_errors.length;
  // if all quotes are declines and and there are medical declines
  if (totalValidQuotes === 0 && medicalDeclines > 0) {
    return {
      quotes: displayQuotes,
      signposted: 'medical_declines_bo',
    };
  }
  return {
    quotes: displayQuotes,
    signposted: false,
  };
};

const getQuotes = (sourceId, application, channel, filters = []) => {
  const clonedApplication = cloneDeep(application);
  return (dispatch, getState) => {
    if (getState().screening.isRecalculatingScore > 0) {
      toastr.error('Re-score in progress', 'Please try again in a few moments');
      return false;
    }

    dispatch(isCalculating(true));
    dispatch(setSignpost(null));
    dispatch(clearQuotes());

    // Set max duration for upsell
    if (get(clonedApplication, 'data.attributes.metadata.scheme_type') === 'single') {
      if (clonedApplication.data.attributes.metadata.trip_duration) {
        clonedApplication.data.attributes.metadata.max_trip_duration =
          clonedApplication.data.attributes.metadata.trip_duration;
      } else {
        const start = parseISO(clonedApplication.data.attributes.metadata.start_date);
        const end = parseISO(clonedApplication.data.attributes.metadata.end_date);
        clonedApplication.data.attributes.metadata.max_trip_duration =
          differenceInDays(end, start) + 1;
      }
    }

    // remove commission overrides for multi calcs
    if (
      get(clonedApplication, 'data.attributes.metadata.overrides.commission_owner', false)
    ) {
      delete clonedApplication.data.attributes.metadata.overrides.commission_owner;
    }

    let endpoint = `sources/${sourceId}:calculate`;

    // If there is a channel (B2C quotes) change the endpoint
    if (channel) {
      endpoint = `channels/${channel}:calculate`;
    }

    if (get(clonedApplication, 'data.attributes.metadata.scheme_type') === 'annual') {
      filters.push('filter[scheme_type]=annual');
    }

    if (filters) {
      endpoint += `?${filters.reduce((carry, filter) => {
        return carry + (carry ? '&' : '') + filter;
      }, '')}`;
    }
    return Axios.post(endpoint, toApi(clonedApplication))
      .then((res) => {
        if (Array.isArray(res.data.meta)) {
          const processedQuotes = processQuotes(
            res.data.meta,
            isOwner(),
            clonedApplication.data.attributes.metadata.scheme_type,
          );
          // unique use case on client request
          // JTC-923: Question 5 of 6 (waiting list) – various rules apply on our panel, if Yes (regardless of medical AP) we need FCA signposting.
          const onWaitingList = get(
            clonedApplication,
            'data.attributes.metadata.medical_declaration_questions.is-awaiting-medical-investigation-treatment.answer',
            false,
          );
          const processQuotesSignpost = onWaitingList
            ? 'medical_declines_bo'
            : processedQuotes.signposted;
          if (processQuotesSignpost) {
            return Promise.resolve(
              dispatch(getSignpost(application.data.id, processQuotesSignpost)),
            )
              .then((resp) => {
                dispatch(setSignpost(resp.data.data.attributes.content));
                return !!processQuotesSignpost;
              })
              .finally(() => {
                dispatch(setQuotes(processedQuotes.quotes));
                dispatch(isCalculating(false));
                return !!processQuotesSignpost;
              });
          }
          dispatch(setQuotes(processedQuotes.quotes));
          dispatch(setSignpost(null));
          dispatch(isCalculating(false));

          return processedQuotes.signposted;
        }
        return null;
      })
      .catch(() => {
        dispatch(isCalculating(false));
      });
  };
};


/*
 * using an es5 function here instead of a es6 arrow to prevent babel transcomiler from complaining
 */
async function getAsyncPremiums(id, target, application, filters = []) {
  const initialResponse = await getPartialPremiums(id, target, application, filters);
  const initialNext = initialResponse?.data?.links.next?.href;
  const allPremiums = initialResponse?.data?.meta?.premiums;
  if (initialNext) {
    let next = initialNext + '&page[limit]=12';
    if (next) {
      do {
        const response = await getPartialPremiums(id, target, application, filters, next );
        const premiums = response?.data?.meta?.premiums ?? [];
        premiums.forEach((premium) => {
          allPremiums.push(premium);
        });
        next = response?.data?.links.next?.href;

      } while (next);
    }
  }


  const processedQuotes = processQuotes(
    allPremiums,
    isOwner(),
    application.data.attributes.metadata.scheme_type,
  );

  const onWaitingList = get(
    application,
    'data.attributes.metadata.medical_declaration_questions.is-awaiting-medical-investigation-treatment.answer',
    false,
  );

  const processQuotesSignpost = onWaitingList
    ? 'medical_declines_bo'
    : processedQuotes.signposted;
  return {
    quotes: processedQuotes.quotes,
    signpost: processQuotesSignpost
  };

}


const getPartialPremiums = (id, target, application, filters = [], next) => {


  // If there is a channel (B2C quotes) change the endpoint
  const url = target === 'sources' ? `sources/${id}:calculate-source` : `channels/${id}:calculate-channel`;
  const endpointFilters = filters.reduce((carry, filter) => {
    return carry + (carry ? '&' : '') + filter;
  }, '');

  const endpoint = next ? next : `${url}?${endpointFilters}&page[limit]=12&page[offset]=0`;
  return Axios.post(endpoint, application);

};

const getSingleInsurerQuotes = (sourceId, application, channel, filters = []) => {
  const clonedApplication = cloneDeep(application);

  return (dispatch) => {
    dispatch(isCalculating(true));
    dispatch({ type: productActions.CLEAR_QUOTES });

    // Set max duration for upsell
    if (get(clonedApplication, 'data.attributes.metadata.scheme_type') === 'single') {
      if (clonedApplication.data.attributes.metadata.trip_duration) {
        clonedApplication.data.attributes.metadata.max_trip_duration =
          clonedApplication.data.attributes.metadata.trip_duration;
      } else {
        const start = parseISO(clonedApplication.data.attributes.metadata.start_date);
        const end = parseISO(clonedApplication.data.attributes.metadata.end_date);
        clonedApplication.data.attributes.metadata.max_trip_duration =
          differenceInDays(end, start) + 1;
      }
    }
    let endpoint = `sources/${sourceId}:calculate`;

    // If there is a channel (B2C quotes) change the endpoint
    if (channel) {
      endpoint = `channels/${channel}:calculate`;
    }

    if (get(clonedApplication, 'data.attributes.metadata.scheme_type') === 'annual') {
      filters.push('filter[scheme_type]=annual');
    }

    if (filters) {
      endpoint += `?${filters.reduce((carry, filter) => {
        return carry + (carry ? '&' : '') + filter;
      }, '')}`;
    }

    return Axios.post(endpoint, toApi(clonedApplication))
      .then((res) => {
        if (Array.isArray(res.data.meta)) {
          const displayQuotes = res.data.meta.reduce((carry, quote) => {
            const hasErrors = quote.errors && quote.errors.length;
            const hasDeclines = quote.declines && quote.declines.length;
            if (isOwner() || (!hasErrors && !hasDeclines)) {
              carry[quote.scheme.id] = { meta: quote };
            }
            return carry;
          }, {});
          dispatch(setQuotes(displayQuotes));
          return displayQuotes;
        }
        return null;
      })

      .finally(() => {
        dispatch(isCalculating(false));
      });
  };
};

const getSingleInsurerMta = (policyId, application) => {
  // deep cloning to format the date for the request
  const localApplication = cloneDeep(application);
  const endpoint = `policies/${policyId}/premiums/mid-term-adjustments`;
  return (dispatch) => {
    dispatch(isCalculating(true));
    dispatch({ type: productActions.CLEAR_QUOTES });
    return Axios.post(endpoint, toApi(localApplication))
      .then((res) => {
        if (Array.isArray(res.data.meta)) {
          const displayQuotes = res.data.meta.reduce((carry, quote) => {
            const hasErrors = quote.errors && quote.errors.length;
            const hasDeclines = quote.declines && quote.declines.length;
            if (isOwner() || (!hasErrors && !hasDeclines)) {
              carry[quote.scheme.id] = { meta: quote };
            }
            return carry;
          }, {});
          dispatch(setQuotes(displayQuotes));
          return displayQuotes;
        }
        return null;
      })
      .finally(() => {
        dispatch(isCalculating(false));
      });
  };
};

const getPremiumIndication = (scheme, formName) => {
  if (isEmpty(scheme)) {
    return null;
  }
  return (dispatch, getState) => {
    dispatch(isUpdatingPremiumIndication(true));
    dispatch(setPremiumIndicationError([]));
    const application = cloneDeep({
      data: formValueSelector(formName)(getState(), 'data'),
      meta: formValueSelector(formName)(getState(), 'meta'),
    });
    return Axios.post(
      `products/schemes/${scheme.id}:calculate?calculate-boltons=true`,
      toApi(application),
    )
      .then((res) => {
        dispatch(setPremiumIndication(res.data));
        dispatch(isUpdatingPremiumIndication(false));
        return true;
      })
      .catch((error) => {
        dispatch(setPremiumIndication([]));
        dispatch(isUpdatingPremiumIndication(false));
        const premiumIndidcationError = {
          errorMessage: error.response,
          schemeId: scheme.id,
        };
        dispatch(setPremiumIndicationError(premiumIndidcationError));
        handleErrorResponse(error);
      });
  };
};

const getPremiumIndicationFromApplication = (schemeId, application) => {
  const clonedApplication = cloneDeep(application);
  return (dispatch) => {
    dispatch(isUpdatingPremiumIndication(true));
    return Axios.post(
      `products/schemes/${schemeId}:calculate?calculate-boltons=true`,
      toApi(clonedApplication),
    )
      .then((res) => {
        dispatch(setPremiumIndication(res.data));
        dispatch(isUpdatingPremiumIndication(false));
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error);
      });
  };
};

const getMTAPremiumIndicationFromApplication = (policyId, application, schemeId) => {
  const clonedApplication = cloneDeep(application);
  return (dispatch) => {
    dispatch(isPremiumAdjusting(false));
    Axios.post(
      `policies/${policyId}/premiums/mid-term-adjustments?calculate-boltons=true`,
      toApi(clonedApplication, true),
    )
      .then((res) => {
        if (res.data.meta && res.data.meta.length) {
          const quote = res.data.meta.find((respQuote) => {
            return get(respQuote, 'scheme.id') === schemeId;
          });
          dispatch(setQuote({ meta: quote }));
        }
        dispatch(isPremiumAdjusting(false));
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error);
      });
  };
};

const getPremiumOverrideCalculation = (scheme, formName) => {
  return (dispatch, getState) => {
    const application = cloneDeep({
      data: formValueSelector(formName)(getState(), 'data'),
    });
    dispatch(isPremiumAdjusting(true));
    return Axios.post(`products/schemes/${scheme.id}:calculate`, toApi(application))
      .then((res) => {
        dispatch(setPremiumOverrideCalculation(res.data));
        dispatch(isPremiumAdjusting(false));
        return true;
      })
      .catch((error) => {
        dispatch(isPremiumAdjusting(false));
        handleErrorResponse(error);
      });
  };
};

const getMTAPremiumOverrideCalculation = (policyId, formName) => {
  return (dispatch, getState) => {
    const application = cloneDeep({
      data: formValueSelector(formName)(getState(), 'data'),
    });
    dispatch(isPremiumAdjusting(true));
    Axios.post(
      `policies/${policyId}/premiums/mid-term-adjustment?calculate-boltons=true`,
      toApi(application, true),
    )
      .then((res) => {
        dispatch(setQuote(res.data));
        dispatch(isPremiumAdjusting(false));

        return true;
      })
      .catch((error) => {
        dispatch(isPremiumAdjusting(false));
        handleErrorResponse(error);
      });
  };
};

const calculateAdjustment = (policyId, application) => {
  // deep cloning to format the date for the request
  const localApplication = cloneDeep(application);

  const isAnnual = localApplication.data.attributes.metadata.scheme_type === 'annual';
  const endpoint = `policies/${policyId}/premiums/mid-term-adjustments?calculate-boltons=true${
    isAnnual ? '&filter[scheme_type]=annual' : ''
  }`;
  return (dispatch) => {
    dispatch(isCalculating(true));
    dispatch({ type: productActions.CLEAR_QUOTES });
    return Axios.post(endpoint, toApi(localApplication))
      .then((res) => {
        if (Array.isArray(res.data.meta)) {
          const processedQuotes = processQuotes(
            res.data.meta,
            isOwner(),
            localApplication.data.attributes.metadata.scheme_type,
          );
          // unique use case on client request
          // JTC-923: Question 5 of 6 (waiting list) – various rules apply on our panel, if Yes (regardless of medical AP) we need FCA signposting.
          const onWaitingList = get(
            localApplication,
            'data.attributes.metadata.medical_declaration_questions.is-awaiting-medical-investigation-treatment.answer',
            false,
          );
          const processQuotesSignpost = onWaitingList
            ? 'medical_declines_bo'
            : processedQuotes.signposted;
          const policySignpost = get(
            application,
            'data.attributes.metadata.storage.policy_signpost',
          );
          const shouldSignpost =
            ['medical_declines_bo', 'screening_errors_bo'].includes(
              processQuotesSignpost,
            ) && processQuotesSignpost !== policySignpost;

          const existingMedical =
            get(localApplication, 'data.attributes.metadata.storage.policy_medical') || 0;
          const allQuotesMedicalIncreased =
            processQuotesSignpost === 'medical_premiums_bo' &&
            Object.values(processedQuotes.quotes).reduce((carry, quote) => {
              const newPremium = quote.meta.mta.new_premium;
              if (newPremium && newPremium.medical) {
                return carry && newPremium.medical > existingMedical;
              }
              return carry;
            }, true);

          if (shouldSignpost || allQuotesMedicalIncreased) {
            return Promise.resolve(
              dispatch(getSignpost(application.data.id, processQuotesSignpost)),
            )
              .then((resp) => {
                dispatch(setSignpost(resp.data.data.attributes.content));
                return processedQuotes.signposted;
              })
              .finally(() => {
                dispatch(setQuotes(processedQuotes.quotes));
                dispatch(isCalculating(false));
                return processedQuotes.signposted;
              });
          }
          dispatch(setQuotes(processedQuotes.quotes));
          dispatch(isCalculating(false));
          dispatch(setSignpost(null));
          return false;
        }
        return false;
      })
      .finally(() => {
        dispatch(isCalculating(false));
      });
  };
};

const getProduct = (id, includes = defaultIncludes) => {
  return (dispatch) => {
    const endpoint = `products/${id}${queryString(includes)}`;

    return Axios.get(endpoint).then((res) => {
      dispatch(setProduct(res.data));
      return true;
    });
  };
};

const updateApplication = (
  application,
  product,
  applicationId,
  cb,
  mta,
  calculate = 'false',
) => {
  const clonedApplication = cloneDeep(application);
  return (dispatch) => {
    if (!cb) {
      //  dispatch(submitSucceeded(false))
      dispatch(isSaving(true));
    }
    if (product) {
      set(clonedApplication, 'data.relationships.product.data.id', product.data.id);
    }
    const END_POINT = `policies/applications/${applicationId}?include=customer,broker,schemes,source,premiums,channel,policy,transactions,product&calculate=${calculate}`;
    return Axios.put(END_POINT, toApi(clonedApplication))
      .then((res) => {
        const app = fromApi(res.data);
        if (cb) {
          cb(app);
        } else {
          dispatch(isSaving(false));
        }
        return app;
      })
      .catch((error) => {
        if (cb) {
          cb(null, true, errorResponseToArray(error));
        } else {
          dispatch(isSaving(false));
          handleErrorResponse(error, 'There was an error saving your quote(s)');
        }
      });
  };
};

const updateApplicationMetadata = (application) => {
  const clonedApplication = cloneDeep(application);
  return () => {
    const applicationId = application.data.id;
    const END_POINT = `policies/applications/${applicationId}?include=customer,broker,schemes,source,premiums,channel,policy,transactions,product`;
    return Axios.patch(END_POINT, toApi(clonedApplication))
      .then((res) => {
        return fromApi(res.data);
      })
      .catch((error) => {
        handleErrorResponse(error, 'There was an error saving your quote(s)');
      });
  };
};

const prepareApplication = (applicationId, premiumId) => {
  return (dispatch) => {
    return Axios.put(`payments/${applicationId}:prepare`, {
      data: {
        relationships: {
          premium: {
            data: {
              id: premiumId,
            },
          },
        },
      },
      meta: {
        channel_key: 'jtc-b2b',
      },
    })
      .then((resp) => {
        dispatch(showPaymentIframe(true, resp.data.meta.transaction_id));

        return resp;
      })
      .catch((error) => {
        handleErrorResponse(error, 'There was an error saving your quote(s)');
      });
  };
};

const saveApplication = (application, product, cb, mta = false, calculate = 'false') => {
  const clonedApplication = cloneDeep(application);
  return () => {
    if (product) {
      set(clonedApplication, 'data.relationships.product.data.id', product.data.id);
    }
    return Axios.post(
      `policies/applications?include=transactions,broker,schemes,premiums,policy,customer,source,channel,product&calculate=${calculate}`,
      toApi(clonedApplication, mta),
    )
      .then((res) => {
        const app = fromApi(res.data);
        if (cb) {
          cb(app);
        }
        return app;
      })
      .catch((error) => {
        if (cb) {
          cb(null, true, errorResponseToArray(error));
        } else {
          handleErrorResponse(error, 'There was an error saving your quote(s)');
        }
      });
  };
};

const updateApplicationEmailAndRedirect = (
  application,
  product,
  applicationId,
  calculate,
) => {
  return (dispatch) => {
    dispatch(submitSucceeded(false));
    return Promise.resolve(
      dispatch(
        updateApplication(application, product, applicationId, null, null, calculate),
      ),
    ).then((res) => {
      dispatch(submitSucceeded(true));
      dispatch(sendCustomerLoginEmail('policies/applications', res.data.id));
      dispatch(push(`/policies/applications/${res.data.id}`));
      dispatch(clearQuotes());
    });
  };
};

const saveApplicationEmailAndRedirect = (application, mta, product, calculate) => {
  return (dispatch) => {
    dispatch(submitSucceeded(false));

    return Promise.resolve(
      dispatch(saveApplication(application, product, null, mta, calculate)),
    ).then((res) => {
      dispatch(submitSucceeded(true));
      dispatch(sendCustomerLoginEmail('policies/applications', res.data.id));
      dispatch(push(`/policies/applications/${res.data.id}`));
      dispatch(clearQuotes());
      return true;
    });
  };
};

const updateApplicationAndRedirect = (application, product, applicationId, calculate) => {
  return (dispatch) => {
    dispatch(submitSucceeded(false));

    return Promise.resolve(
      dispatch(
        updateApplication(application, product, applicationId, null, null, calculate),
      ),
    ).then((res) => {
      dispatch(submitSucceeded(true));
      dispatch(push(`/policies/applications/${res.data.id}`));
      dispatch(clearQuotes());
      return true;
    });
  };
};

const saveApplicationAndRedirect = (
  application,
  product,
  sendEmail = false,
  calculate,
) => {
  return (dispatch) => {
    dispatch(submitSucceeded(false));
    dispatch(isSaving(true));
    return Promise.resolve(
      dispatch(saveApplication(application, product, null, null, calculate)),
    )
      .then((res) => {
        dispatch(submitSucceeded(true));
        if (sendEmail) {
          dispatch(sendCustomerLoginEmail('policies/applications', res.data.id));
        }
        dispatch(push(`/policies/applications/${res.data.id}`));
        dispatch(clearQuotes());
      })
      .finally(() => {
        dispatch(isSaving(false));
      });
  };
};

const issuePolicy = (applicationId, values, sendEmail = false) => {
  return (dispatch) => {
    dispatch(isIssuing(true));
    dispatch(submitSucceeded(false));

    return Axios.post(`policies/applications/${applicationId}:issue`, values)
      .then((res) => {
        dispatch(submitSucceeded(true));
        if (sendEmail) {
          dispatch(sendCustomerLoginEmail('policies', res.data.data.id));
        }
        dispatch(push(`/policies/issued/${res.data.data.id}`));
        dispatch(isIssuing(false));
        return true;
      })
      .catch((error) => {
        dispatch(isIssuing(false));
        handleErrorResponse(error, 'There was an error when issuing your policy');
      });
  };
};

const redirectAfterSuccessfulCardPayment = (policyId) => {
  return (dispatch) => {
    dispatch(isIssuing(false));
    dispatch(submitSucceeded(true));
    dispatch(push(`/policies/issued/${policyId}`));
  };
};

const issuePolicyMta = (applicationId, values, sendEmail = false) => {
  return (dispatch) => {
    dispatch(isIssuing(true));
    dispatch(submitSucceeded(false));

    return Axios.post(`policies/applications/${applicationId}:issue`, values)
      .then((res) => {
        dispatch(submitSucceeded(true));
        if (sendEmail) {
          dispatch(sendEndorsementEmail(res.data.data.id));
        }
        dispatch(push(`/policies/issued/${res.data.data.id}`));
        dispatch(isIssuing(false));
        return true;
      })
      .catch((error) => {
        dispatch(isIssuing(false));
        handleErrorResponse(error, 'There was an error when issuing your policy');
      });
  };
};

const setSanctionsCheckInProgress = (status) => {
  return (dispatch) => {
    dispatch({
      type: productActions.SANCTIONS_CHECK_IN_PROGRESS,
      status,
    });
  };
};

const setSanctions = (key, status) => {
  return {
    type: productActions.SET_SANCTIONS,
    status,
    key,
  };
};

const removeSanction = (key) => {
  return {
    type: productActions.REMOVE_SANCTION,
    key,
  };
};
const checkSanctions = (traveller, cb) => {
  return (dispatch) => {
    const firstName = get(traveller, 'first_name');
    const lastName = get(traveller, 'last_name');
    const dob = get(traveller, 'dob');
    const disableSanctionsCheck = !firstName && !lastName && !dob;

    if (disableSanctionsCheck) {
      if (cb) {
        cb(null);
      }
      return true;
    }

    const data = {
      sanction_id: traveller.sanction_id,
      first_name: traveller.first_name,
      last_name: traveller.last_name,
      dob: dateToApi(traveller.dob),
      address_country: 'United Kingdom',
      address_line_1: traveller.address ? traveller.address.line1 : null,
      address_line_2: traveller.address ? traveller.address.line2 : null,
      address_town: traveller.address ? traveller.address.town : null,
      address_county: traveller.address ? traveller.address.county : null,
      address_postcode: traveller.address ? traveller.address.postcode : null,
      title: traveller.title,
    };

    dispatch(setSanctionsCheckInProgress(true));
    return Axios.post('sanction/verify', { meta: data })
      .then((response) => {
        dispatch(setSanctionsCheckInProgress(false));
        if (cb) {
          cb(response.data.meta.sanction);
        }
        return response.data.meta.sanction;
      })
      .catch((error) => {
        dispatch(setSanctionsCheckInProgress(false));
        if (cb) {
          cb(false);
        }
        handleErrorResponse(error, 'Failed to check customer sanctions');
      });
  };
};

const checkMultipleSanctions = (values) => {
  return (dispatch) => {
    dispatch(setSanctionsCheckInProgress(true));
    return Axios.post('public/sanctions-verify-multiple', values)
      .then((response) => {
        return response.data;
      })
      .finally(() => {
        dispatch(setSanctionsCheckInProgress(false));
      });
  };
};

const muteCall = () => {
  return () => {
    return Axios.post('call-recording/mute').then((response) => {
      const info = response?.data?.meta;

      const isMuted = info.is_muted;
      const hasCall = info.has_call;
      if (!hasCall) {
        return toastr.error(
          'Call Not Muted',
          `Call recording not found for ${info?.ext}`,
        );
      }
      if (!isMuted) {
        return toastr.error(
          'Call Not Muted',
          `Call recording could not be muted for ${info?.ext}`,
        );
      }
      return toastr.success('Call Muted', `Call recording muted for ${info?.ext}`);
    });
  };
};

const resumeCall = () => {
  return (dispatch) => {
    return Axios.post('call-recording/resume')
      .then((response) => {
        const info = response?.data?.meta;

        const isMuted = info.is_muted;
        const hasCall = info.has_call;
        if (!hasCall) {
          return toastr.error(
            'Call Recording Not Resumed',
            `Call recording not found for  ${info?.ext}`,
          );
        }

        if (isMuted) {
          return toastr.error(
            'Call Recording Not Resumed',
            `Call recording could not be resumed for ${info?.ext} `,
          );
        }

        toastr.success(
          'Call Recording Resumed',
          `Call recording resumed for ${info?.ext}`,
        );
      })
      .finally(() => {
        dispatch(setSanctionsCheckInProgress(false));
      });
  };
};

export const actions = {
  isCalculating,
  isPremiumAdjusting,
  clearData,
  isUpdatingPremiumIndication,
  submitSucceeded,
  isSaving,
  isIssuing,
  setSource,
  setRegions,
  setSchemes,
  setSchemesWithIncludes,
  setSignpost,
  setProduct,
  setPremiumOverrideCalculation,
  setPremiumIndication,
  clearNextQuoteReference,
  setNextQuoteReference,
  setQuote,
  setQuotes,
  clearQuotes,
  setPremiumIndicationError,
  getSource,
  getRegions,
  getProductDocuments,
  getNextQuoteReference,
  getSchemes,
  getSchemesWithIncludes,
  getSignpost,
  processQuotes,
  getQuotes,
  getSingleInsurerQuotes,
  getSingleInsurerMta,
  getPremiumIndication,
  getPremiumIndicationFromApplication,
  getMTAPremiumIndicationFromApplication,
  getPremiumOverrideCalculation,
  getMTAPremiumOverrideCalculation,
  calculateAdjustment,
  getProduct,
  updateApplication,
  updateApplicationMetadata,
  saveApplication,
  updateApplicationEmailAndRedirect,
  saveApplicationEmailAndRedirect,
  updateApplicationAndRedirect,
  saveApplicationAndRedirect,
  issuePolicy,
  issuePolicyMta,
  setSanctionsCheckInProgress,
  checkMultipleSanctions,
  setSanctions,
  removeSanction,
  checkSanctions,
  prepareApplication,
  showPaymentIframe,
  redirectAfterSuccessfulCardPayment,
  muteCall,
  resumeCall,
};
